"use client";

import React, { useState, useRef, useMemo, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTenancy } from "@/contexts/TenancyContext";
import { createProcess, ProcessType, ProcessRepeatFrequency, ProcessRepeatConfig } from "@/services/processService";
import { toast } from "sonner";
import { Loader2, Upload, X, FileText, Calendar, CalendarClock, Repeat, Check } from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faXmark, 
  faBarsProgress, 
  faMicrophone, 
  faFileLines, 
  faUpload,
  faSparkles,
  faCalendarClock,
  faRepeat
} from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { cn } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";
import { cleanDateAndTimeObject, toLocalDateString } from "@shared/renderDate";
import { SingleUserCombobox } from "@/components/ui/SingleUserCombobox";
import { getAuthUID } from "@/lib/authState";

interface AddProcessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const processTypes: { value: ProcessType; label: string; icon: any; description: string; color: string }[] = [
  {
    value: 'documented',
    label: 'Documented',
    icon: faFileLines,
    description: 'A formally documented process with supporting documentation',
    color: 'emerald',
  },
  {
    value: 'verbal',
    label: 'Verbal',
    icon: faMicrophone,
    description: 'A process communicated verbally without formal documentation',
    color: 'blue',
  },
];


const frequencyOptions: { value: ProcessRepeatFrequency; label: string }[] = [
  { value: 'never', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

export function AddProcessDialog({ open, onOpenChange, onSuccess }: AddProcessDialogProps) {
  const { activeTenancy, userIdToName } = useTenancy();
  const companyID = activeTenancy?.id || (window as any).companyID;
  const currentUserId = getAuthUID() || '';
  
  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [processType, setProcessType] = useState<ProcessType | null>(null);
  const [owner, setOwner] = useState(currentUserId);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Set default owner to current user when dialog opens
  useEffect(() => {
    if (open && !owner && currentUserId) {
      setOwner(currentUserId);
    }
  }, [open, currentUserId]);
  
  // Scheduling state
  const [repeatFrequency, setRepeatFrequency] = useState<ProcessRepeatFrequency>('never');
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [reviewDate, setReviewDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const isRecurring = repeatFrequency !== 'never';
  
  // Auto-set start date when switching to recurring
  useEffect(() => {
    if (isRecurring && !startDate) {
      const base = cleanDateAndTimeObject(reviewDate ?? new Date()) ?? new Date();
      setStartDate(base);
    }
    if (!isRecurring) {
      if (!reviewDate) {
        setReviewDate(cleanDateAndTimeObject(new Date()) ?? new Date());
      }
      setStartDate(null);
      setEndDate(null);
    }
  }, [isRecurring, reviewDate, startDate]);
  
  // Schedule summary for display
  const scheduleSummary = useMemo(() => {
    if (!isRecurring || !startDate) return null;
    const intervalLabel = repeatInterval === 1 ? 'Repeats' : `Repeats every ${repeatInterval}`;
    const unit = repeatFrequency === 'daily'
      ? repeatInterval === 1 ? 'day' : 'days'
      : repeatFrequency === 'weekly'
        ? repeatInterval === 1 ? 'week' : 'weeks'
        : repeatFrequency === 'monthly'
          ? repeatInterval === 1 ? 'month' : 'months'
          : repeatFrequency === 'quarterly'
            ? repeatInterval === 1 ? 'quarter' : 'quarters'
            : repeatInterval === 1 ? 'year' : 'years';
    const endText = endDate ? ` until ${toLocalDateString(endDate)}` : '';
    return `${intervalLabel} ${repeatInterval === 1 ? '' : unit} starting ${toLocalDateString(startDate)}${endText}`;
  }, [isRecurring, startDate, endDate, repeatInterval, repeatFrequency]);
  
  // Validation
  const canSave = name.trim().length > 0 && processType !== null && !saving;
  
  // Reset form
  const resetForm = () => {
    setName("");
    setDescription("");
    setProcessType(null);
    setOwner("");
    setFile(null);
    setRepeatFrequency('never');
    setRepeatInterval(1);
    setReviewDate(null);
    setStartDate(null);
    setEndDate(null);
  };
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };
  
  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };
  
  // Handle save
  const handleSave = async () => {
    if (!canSave || !companyID || !processType) return;
    
    setSaving(true);
    try {
      // Build repeat config if recurring
      const repeat: ProcessRepeatConfig | undefined = isRecurring && startDate ? {
        frequency: repeatFrequency,
        interval: repeatInterval,
        startDate: startDate,
        endDate: endDate,
      } : undefined;
      
      await createProcess(companyID, {
        name: name.trim(),
        description: description.trim(),
        type: processType,
        status: 'Active', // Default status for new processes
        owner: owner.trim() || undefined,
        repeat,
        reviewDate: repeatFrequency === 'never' ? undefined : (!isRecurring ? reviewDate || undefined : undefined),
        file: processType === 'documented' ? file || undefined : undefined,
      });
      
      toast.success("Process created successfully");
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("[AddProcessDialog] Failed to create process:", error);
      toast.error("Failed to create process");
    } finally {
      setSaving(false);
    }
  };
  
  // Handle close
  const handleClose = () => {
    if (!saving) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-3xl! w-3xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={saving}
            className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {/* Header */}
          <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
              <FontAwesomeIcon icon={faBarsProgress} className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">Add New Process</h2>
            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
              Define a new organizational process. Processes can be verbal or documented.
            </p>
          </div>

          {/* Form Content - scrollable */}
          <div className="p-8 flex-1 min-h-0 overflow-y-auto space-y-6">
            {/* Process Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">
                Process Type <span className="text-destructive">*</span>
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {processTypes.map((type) => {
                  const isSelected = processType === type.value;
                  return (
                    <div
                      key={type.value}
                      onClick={() => setProcessType(type.value)}
                      style={{
                        background: isSelected ? 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15), rgba(var(--primary-rgb), 0.05))' : '',
                        backdropFilter: 'blur(8px)'
                      }}
                      className={cn(
                        "relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all duration-300 transform hover:scale-[1.02]",
                        isSelected
                          ? "border-2 border-primary shadow-lg"
                          : "bg-white/5 hover:bg-white/10 dark:bg-gray-800/30 dark:hover:bg-gray-800/50 border border-white/10 shadow-sm hover:shadow-md"
                      )}
                    >
                      {/* Light accent when selected */}
                      {isSelected && (
                        <div className="absolute inset-0 bg-linear-to-tr from-primary/5 via-transparent to-primary/10 pointer-events-none" />
                      )}
                      
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center shadow-sm z-20">
                          <Check className="h-3 w-3 text-primary-foreground" />
                        </div>
                      )}
                      
                      <div className="relative z-10">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center mb-3",
                          type.color === 'emerald' && "bg-emerald-500/15",
                          type.color === 'blue' && "bg-blue-500/15",
                          type.color === 'amber' && "bg-amber-500/15"
                        )}>
                          <FontAwesomeIcon 
                            icon={type.icon} 
                            className={cn(
                              "w-5 h-5",
                              type.color === 'emerald' && "text-emerald-500",
                              type.color === 'blue' && "text-blue-500",
                              type.color === 'amber' && "text-amber-500"
                            )} 
                          />
                        </div>
                        <div className="font-semibold text-foreground">{type.label}</div>
                        <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Process Name */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                  <FontAwesomeIcon icon={faBarsProgress} className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <Label htmlFor="processName" className="block text-sm font-semibold text-foreground mb-1">
                    Process Name <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    A clear, descriptive name for this process
                  </p>
                  <Input
                    id="processName"
                    placeholder="e.g., Employee Onboarding Process"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
              <Label htmlFor="description" className="block text-sm font-semibold text-foreground mb-1">
                Description
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Describe what this process involves and its purpose
              </p>
              <Textarea
                id="description"
                placeholder="Describe the process steps, purpose, and any relevant details..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-white/5 border border-primary/10 min-h-[100px] focus:border-primary/50"
              />
            </div>

            {/* File Upload - Only for Documented processes */}
            {processType === 'documented' && (
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <Label className="block text-sm font-semibold text-foreground mb-1">
                  Process Document
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Upload the document that describes this process
                </p>
                
                {file ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <FileText className="w-8 h-8 text-emerald-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFile(null)}
                      className="shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-primary/20 rounded-lg p-8 text-center cursor-pointer hover:border-primary/40 hover:bg-white/5 transition-all"
                  >
                    <FontAwesomeIcon icon={faUpload} className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, DOC, DOCX, or other document formats
                    </p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.md,.xls,.xlsx,.ppt,.pptx"
                />
              </div>
            )}

            {/* Process Owner */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
              <Label className="block text-sm font-semibold text-foreground mb-3">
                Process Owner
              </Label>
              <SingleUserCombobox
                selected={owner}
                setSelected={setOwner}
                placeholder="Select owner..."
                className="bg-white/5 border-primary/10 h-11"
              />
            </div>

            {/* Scheduling Section */}
            <div className="rounded-xl border border-primary/20 bg-linear-to-br from-primary/5 to-primary/15 backdrop-blur-sm p-6 space-y-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCalendarClock} className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <Label className="block text-sm font-semibold text-foreground mb-1">
                    Review Schedule
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Set up when this process should be reviewed. Recurring processes will appear in your compliance calendar.
                  </p>
                </div>
              </div>

              {/* Schedule Summary Badge - displayed above date fields to match Policies */}
              {scheduleSummary && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                  <FontAwesomeIcon icon={faRepeat} className="w-4 h-4 text-cyan-500" />
                  <p className="text-sm text-cyan-600 dark:text-cyan-400 font-medium">
                    {scheduleSummary}
                  </p>
                </div>
              )}

              {/* Frequency Selection & Date Pickers - Inline Layout */}
              {!isRecurring ? (
                /* Non-recurring: Full width grid layout */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Frequency Dropdown */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Recurrence
                    </Label>
                    <Select
                      value={repeatFrequency}
                      onValueChange={(value) => {
                        const typed = value as ProcessRepeatFrequency;
                        setRepeatFrequency(typed);
                        if (typed === 'never') {
                          setStartDate(null);
                          setEndDate(null);
                        } else {
                          const base = cleanDateAndTimeObject(startDate ?? reviewDate ?? new Date()) ?? new Date();
                          setStartDate(base);
                          if (!reviewDate) {
                            setReviewDate(base);
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full rounded-lg">
                        <SelectValue placeholder="Does not repeat" />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Review Date */}
                  <div className="space-y-2">
                    <Label className={`text-xs font-medium uppercase tracking-wider ${repeatFrequency === 'never' ? 'text-muted-foreground/50' : 'text-muted-foreground'}`}>
                      Review Date
                    </Label>
                    <DatePicker
                      date={reviewDate ?? undefined}
                      setDate={(date) => setReviewDate(date ?? null)}
                      readOnly={repeatFrequency === 'never'}
                      className={`bg-white/5 border-primary/10 h-11 rounded-lg w-full ${repeatFrequency === 'never' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>
              ) : (
                /* Recurring: Flexible row layout */
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:flex-wrap">
                  {/* Frequency Dropdown */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Recurrence
                    </Label>
                    <Select
                      value={repeatFrequency}
                      onValueChange={(value) => {
                        const typed = value as ProcessRepeatFrequency;
                        setRepeatFrequency(typed);
                        if (typed === 'never') {
                          setStartDate(null);
                          setEndDate(null);
                        } else {
                          const base = cleanDateAndTimeObject(startDate ?? reviewDate ?? new Date()) ?? new Date();
                          setStartDate(base);
                          if (!reviewDate) {
                            setReviewDate(base);
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full sm:w-[180px] rounded-lg">
                        <SelectValue placeholder="Does not repeat" />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Recurring: Interval controls inline */}
                  <div className="flex items-end gap-2">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Every
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        value={repeatInterval}
                        onChange={(e) => {
                          const numeric = Number(e.target.value);
                          setRepeatInterval(Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 1);
                        }}
                        className="bg-white/5 border-primary/10 h-11 w-[70px] rounded-lg text-center"
                      />
                    </div>
                    <span className="text-sm text-muted-foreground h-11 flex items-center">
                      {repeatFrequency === 'daily'
                        ? 'day(s)'
                        : repeatFrequency === 'weekly'
                          ? 'week(s)'
                          : repeatFrequency === 'monthly'
                            ? 'month(s)'
                            : repeatFrequency === 'quarterly'
                              ? 'quarter(s)'
                              : 'year(s)'}
                    </span>
                  </div>
                </div>
              )}

              {/* Recurring: Start/End Date Row */}
              {isRecurring && (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Start Date
                    </Label>
                    <DatePicker
                      date={startDate ?? undefined}
                      setDate={(date) => setStartDate(date ?? null)}
                      className="bg-white/5 border-primary/10 h-11 rounded-lg w-full sm:w-[180px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      End Date <span className="text-muted-foreground/60">(optional)</span>
                    </Label>
                    <DatePicker
                      date={endDate ?? undefined}
                      setDate={(date) => setEndDate(date ?? null)}
                      className="bg-white/5 border-primary/10 h-11 rounded-lg w-full sm:w-[180px]"
                    />
                  </div>
                </div>
              )}

            </div>

            {/* Info Note */}
            <div className="rounded-lg bg-primary/5 border border-primary/10 p-4 flex items-start gap-3">
              <FontAwesomeIcon icon={faSparkles} className="w-5 h-5 text-cyan-500 shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Tip:</span> Documented processes are stored in your document library and can be linked to controls for compliance tracking.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 px-8 py-5 bg-card flex justify-between items-center shrink-0">
            <p className="text-xs text-muted-foreground">
              {!processType 
                ? "Select a process type to continue" 
                : !name.trim()
                  ? "Enter a process name to continue"
                  : "Ready to create process"}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
              <Button disabled={!canSave} onClick={handleSave} className="min-w-[100px]">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Process
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddProcessDialog;
